import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Check } from "lucide-react";
import { toast } from "sonner";
import api, { money, errMsg } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [done, setDone] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addr, setAddr] = useState({
    name: user?.name || "", email: user?.email || "", line1: "", city: "", zip: "", country: "France", phone: "",
  });
  const [card, setCard] = useState({ num: "", exp: "", cvc: "" });
  const shipping = subtotal >= 200 ? 0 : 8;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((it) => ({ product_id: it.product_id, variant_id: it.variant_id, quantity: it.quantity })),
        address: addr, email: addr.email,
      });
      clear();
      setDone(data.order);
      window.scrollTo(0, 0);
    } catch (err) { toast.error(errMsg(err)); }
    setLoading(false);
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <div className="w-16 h-16 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center mx-auto"><Check size={28} /></div>
        <h1 className="font-display text-4xl font-light mt-6">Merci pour votre commande</h1>
        <p className="text-neutral-500 mt-3">Commande <span className="font-mono">#{done.id.slice(0, 8)}</span> confirmée — {money(done.total)}</p>
        <p className="text-xs text-neutral-400 mt-2">Paiement simulé (mode démo). Stripe sera branché ultérieurement.</p>
        <div className="flex gap-3 justify-center mt-8">
          {user && user.id && <button onClick={() => nav("/compte")} className="btn btn-dark">Mes commandes</button>}
          <button onClick={() => nav("/catalogue")} className="btn btn-outline">Continuer mes achats</button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return <div className="py-24 text-center"><p className="text-neutral-500">Votre panier est vide.</p><button onClick={() => nav("/catalogue")} className="btn btn-dark mt-6">Découvrir</button></div>;
  }

  const field = "w-full border border-neutral-200 px-3 py-3 text-sm focus:border-neutral-900 outline-none";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-16 py-14">
      <h1 className="font-display text-4xl lg:text-5xl font-light mb-10">Finaliser</h1>
      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_380px] gap-12">
        <div className="space-y-10">
          <div>
            <span className="over text-xs">Coordonnées & livraison</span>
            <div className="grid sm:grid-cols-2 gap-4 mt-5">
              <input required placeholder="Nom complet" value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })} className={field} data-testid="checkout-name" />
              <input required type="email" placeholder="E-mail" value={addr.email} onChange={(e) => setAddr({ ...addr, email: e.target.value })} className={field} data-testid="checkout-email" />
              <input required placeholder="Adresse" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} className={`${field} sm:col-span-2`} data-testid="checkout-address" />
              <input required placeholder="Code postal" value={addr.zip} onChange={(e) => setAddr({ ...addr, zip: e.target.value })} className={field} data-testid="checkout-zip" />
              <input required placeholder="Ville" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} className={field} data-testid="checkout-city" />
              <input required placeholder="Pays" value={addr.country} onChange={(e) => setAddr({ ...addr, country: e.target.value })} className={field} />
              <input placeholder="Téléphone" value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} className={field} />
            </div>
          </div>
          <div>
            <span className="over text-xs flex items-center gap-2"><Lock size={13} /> Paiement sécurisé (démo)</span>
            <div className="grid sm:grid-cols-2 gap-4 mt-5">
              <input required placeholder="Numéro de carte" value={card.num} onChange={(e) => setCard({ ...card, num: e.target.value })} className={`${field} sm:col-span-2`} data-testid="checkout-card" />
              <input required placeholder="MM / AA" value={card.exp} onChange={(e) => setCard({ ...card, exp: e.target.value })} className={field} />
              <input required placeholder="CVC" value={card.cvc} onChange={(e) => setCard({ ...card, cvc: e.target.value })} className={field} />
            </div>
            <p className="text-xs text-neutral-400 mt-3">Aucun paiement réel n'est effectué. Utilisez des valeurs fictives.</p>
          </div>
        </div>

        <div className="bg-[#FAFAFA] p-7 h-fit">
          <span className="over text-xs">Votre commande</span>
          <div className="mt-5 space-y-4 max-h-64 overflow-y-auto no-scrollbar">
            {items.map((it, i) => (
              <div key={i} className="flex gap-3">
                <img src={it.image} alt="" className="w-14 h-18 object-cover bg-neutral-100" />
                <div className="flex-1 text-sm">
                  <p className="leading-tight">{it.name}</p>
                  <p className="text-xs text-neutral-500">{it.variant_label} × {it.quantity}</p>
                </div>
                <span className="text-sm">{money(it.price * it.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="hairline my-5" />
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-neutral-500">Sous-total</span><span>{money(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">Livraison</span><span>{shipping === 0 ? "Offerte" : money(shipping)}</span></div>
            <div className="flex justify-between text-base pt-2"><span>Total</span><span>{money(subtotal + shipping)}</span></div>
          </div>
          <button disabled={loading} className="btn btn-magenta w-full mt-6" data-testid="checkout-pay-button">{loading ? "Traitement…" : `Payer ${money(subtotal + shipping)}`}</button>
        </div>
      </form>
    </div>
  );
}
